import { useEffect, useRef, useState } from 'react';
import { clients as clientsApi } from '../api/client.js';

/**
 * Mini Leaflet map with a draggable marker for geocoding correction.
 * When dropped, sends PUT to update client lat/lng.
 * @param {{ clientId: string, lat: number|null, lng: number|null, onCoordsUpdate: (lat: number, lng: number) => void }} props
 */
export default function ClientMap({ clientId, lat, lng, onCoordsUpdate }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!mapRef.current || typeof window.L === 'undefined') return;
    if (!lat || !lng) return;

    const L = window.L;

    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView([lat, lng], 16);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; CARTO &copy; OSM',
        maxZoom: 19,
      }).addTo(mapInstance.current);
    } else {
      mapInstance.current.setView([lat, lng], 16);
    }

    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(mapInstance.current);
      markerRef.current.on('dragend', handleDragEnd);
    }

    return () => {
      // Cleanup on unmount
    };
  }, [lat, lng]);

  // Separate cleanup for unmount
  useEffect(() => {
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  /** @param {object} e - Leaflet drag event */
  const handleDragEnd = async (e) => {
    const position = e.target.getLatLng();
    const newLat = Math.round(position.lat * 1e6) / 1e6;
    const newLng = Math.round(position.lng * 1e6) / 1e6;

    setSaving(true);
    setError('');
    try {
      await clientsApi.update(clientId, { lat: newLat, lng: newLng, geocodingConfidence: 1.0 });
      onCoordsUpdate(newLat, newLng);
    } catch (err) {
      setError(err.message || 'Erreur de sauvegarde');
      // Revert marker position
      if (markerRef.current && lat && lng) {
        markerRef.current.setLatLng([lat, lng]);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!lat || !lng) {
    return (
      <div className="bg-gray-100 rounded-xl p-4 text-center text-sm text-gray-400">
        Pas de coordonnées GPS pour ce client.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="flex justify-between items-center px-4 pt-3 pb-1">
        <h3 className="font-semibold text-sm">Position GPS</h3>
        {saving && <span className="text-xs text-blue-500">Sauvegarde...</span>}
      </div>
      {error && <p className="text-xs text-red-500 px-4">{error}</p>}
      <p className="text-xs text-gray-400 px-4 mb-2">Déplacez le marqueur pour corriger la position.</p>
      <div ref={mapRef} className="w-full h-48" />
    </div>
  );
}
